import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'antd';
import React, { useEffect, useState } from 'react';

import { COLOR, SPACING } from '../../theme/tokens';
import { Item } from './item';
import { type ISortableListItem } from './types';

export * from './types';

export function SortableList({
  records,
  id,
  saveCallback,
  ItemTemplate,
}: {
  records: ISortableListItem[];
  id: string;
  saveCallback: (items: ISortableListItem[]) => void;
  ItemTemplate: ({
    id,
    record,
  }: {
    id: UniqueIdentifier;
    record: ISortableListItem;
  }) => React.ReactElement;
}) {
  const [items, setItems] = useState<ISortableListItem[]>(records);
  const { setNodeRef } = useDroppable({ id });

  useEffect((): void => {
    const recordsSorted: ISortableListItem[] = records.sort(
      (a: ISortableListItem, b: ISortableListItem): number =>
        a.position - b.position,
    );

    setItems(recordsSorted);
  }, [records]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((ii: ISortableListItem[]) => {
        const oldIndex: number = ii.findIndex(
          (i: ISortableListItem): boolean => i.id === active.id,
        );
        const newIndex: number = ii.findIndex(
          (i: ISortableListItem): boolean => i.id === over.id,
        );

        const itemsMoved: ISortableListItem[] = arrayMove(
          ii,
          oldIndex,
          newIndex,
        );

        return itemsMoved.map((item: ISortableListItem, index: number) => {
          return { ...item, position: index + 1 };
        });
      });
    }
  }

  const droppableStyle = {
    padding: `${SPACING.lg}px ${SPACING.sm}px`,
    border: `1px solid ${COLOR.borderDefault}`,
    borderRadius: 6,
    minWidth: '100%',
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e: DragEndEvent): void => handleDragEnd(e)}
      >
        <SortableContext
          id={id}
          items={items}
          strategy={verticalListSortingStrategy}
        >
          <div ref={setNodeRef} style={droppableStyle}>
            {items.map((item: ISortableListItem): React.ReactElement => (
              <Item
                key={item.id}
                id={item.id}
                record={item}
                ItemTemplate={ItemTemplate}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button
        type="primary"
        style={{ marginTop: SPACING.md }}
        onClick={(): void => {
          saveCallback(items);
        }}
      >
        Save
      </Button>
    </>
  );
}
